import type { ApiMessage } from '../api/types';
import type { MessageKey } from './keys/messageKey';
import { AudioOrigin } from '../types';

import { IS_SAFARI } from './browser/windowEnvironment';
import { getMessageServerKey, parseMessageKey } from './keys/messageKey';
import { isSafariPatchInProgress, patchSafariProgressiveAudio } from './patchSafariProgressiveAudio';

// ─── Constants ────────────────────────────────────────────────────────────────

export const YOUTUBE_CHAT_ID = '-999999';

// Navigation queues and the persisted playlist are bounded so a long listening/scrolling session
// can't grow memory or localStorage without limit.
const MAX_QUEUE_SIZE = 300;
const MAX_PERSISTED_PLAYLIST = 50;

// ─── Types ────────────────────────────────────────────────────────────────────

export type TrackId = `${MessageKey}-${number}`;

export type TrackEntry = {
  id: TrackId;
  audio: HTMLAudioElement;
  type: 'voice' | 'audio' | 'oneTimeVoice';
  src?: string;
  chatId: string;
  cleanupListeners: () => void;
  refCount: number;
};

export type AudioEvent =
  | { type: 'play'; trackId: TrackId }
  | { type: 'pause'; trackId: TrackId }
  | { type: 'stop' }
  | { type: 'trackChange'; prevTrackId?: TrackId; nextTrackId: TrackId }
  | { type: 'timeUpdate'; trackId: TrackId; currentTime: number; duration: number }
  | { type: 'ended'; trackId: TrackId }
  | { type: 'error'; trackId: TrackId; error: unknown };

export type AudioEventListener = (event: AudioEvent) => void;

// ─── Utilities ────────────────────────────────────────────────────────────────

export function makeTrackId(message: ApiMessage): TrackId | undefined {
  const key = getMessageServerKey(message);
  if (!key) {
    return undefined;
  }
  return `${key}-${message.date}`;
}

export function splitTrackId(trackId: TrackId) {
  // Guard the match: a corrupted/legacy id (e.g. from stale localStorage) must not throw here,
  // since this runs inside unguarded sorts/comparators.
  const match = trackId.match(/^msg(-?\d+)-(\d+)/);
  const messageKey = (match ? match[0] : trackId) as MessageKey;
  const date = Number(trackId.split('-').pop());
  return { messageKey, date };
}

export function parseTrackChatId(trackId: TrackId): string {
  const { messageKey } = splitTrackId(trackId);
  return parseMessageKey(messageKey).chatId;
}

// Descending order by date, then by messageKey for stable sort
export function trackIdComparator(one?: TrackId, two?: TrackId): number {
  if (!one || !two) return 0;
  const { date: dateOne, messageKey: keyOne } = splitTrackId(one);
  const { date: dateTwo, messageKey: keyTwo } = splitTrackId(two);
  const diff = dateTwo - dateOne;
  return diff === 0 ? keyTwo.localeCompare(keyOne) : diff;
}

// ─── AudioController ──────────────────────────────────────────────────────────

export type RepeatMode = 'none' | 'one' | 'all';

export class AudioController {
  private tracks = new Map<TrackId, TrackEntry>();
  private voiceQueue: TrackId[] = [];
  private musicQueue: TrackId[] = [];
  private currentTrackId: TrackId | undefined;

  // A track restored from a previous session while paused; its first autoplay is suppressed so a
  // page reload doesn't resume music the user had paused.
  private restoredPausedTrackId: TrackId | undefined;

  private isAutoplayUnlocked = false;
  repeatMode: RepeatMode = 'none';
  shuffle = false;

  // Global listeners (all tracks)
  private globalListeners: AudioEventListener[] = [];
  // Per-track listeners
  private trackListeners = new Map<TrackId, AudioEventListener[]>();

  // ─── Event System ──────────────────────────────────────────────────────────

  on(listener: AudioEventListener): void {
    if (!this.globalListeners.includes(listener)) {
      this.globalListeners.push(listener);
    }
  }

  off(listener: AudioEventListener): void {
    this.globalListeners = this.globalListeners.filter((l) => l !== listener);
  }

  onTrack(trackId: TrackId, listener: AudioEventListener): void {
    const existing = this.trackListeners.get(trackId) ?? [];
    if (!existing.includes(listener)) {
      this.trackListeners.set(trackId, [...existing, listener]);
    }
  }

  offTrack(trackId: TrackId, listener: AudioEventListener): void {
    const existing = this.trackListeners.get(trackId);
    if (!existing) return;
    const filtered = existing.filter((l) => l !== listener);
    if (filtered.length > 0) {
      this.trackListeners.set(trackId, filtered);
    } else {
      this.trackListeners.delete(trackId);
    }
  }

  private emit(event: AudioEvent): void {
    // Auto-save on significant events
    if (event.type === 'play' || event.type === 'pause' || event.type === 'stop'
      || event.type === 'trackChange' || event.type === 'ended') {
      this.saveState();
    }

    const trackId = 'trackId' in event ? event.trackId : undefined;
    for (const listener of this.globalListeners) {
      listener(event);
    }
    if (trackId) {
      const perTrack = this.trackListeners.get(trackId);
      if (perTrack) {
        for (const listener of perTrack) {
          listener(event);
        }
      }
    }
  }

  // ─── Track Registration ────────────────────────────────────────────────────

  registerTrack(trackId: TrackId, type: TrackEntry['type']): void {
    const existing = this.tracks.get(trackId);
    if (existing) {
      existing.refCount++;
      return;
    }

    const audio = new Audio();
    const chatId = parseTrackChatId(trackId);

    // DOM event → AudioController event forwarding
    const onPlay = () => {
      if (!this.tracks.has(trackId)) return;
      if (isSafariPatchInProgress(audio)) return;
      this.emit({ type: 'play', trackId });
    };
    const onPause = () => {
      if (!this.tracks.has(trackId)) return;
      if (isSafariPatchInProgress(audio)) return;
      this.emit({ type: 'pause', trackId });
    };
    const onTimeUpdate = () => {
      if (!this.tracks.has(trackId)) return;
      if (isSafariPatchInProgress(audio)) return;
      this.emit({
        type: 'timeUpdate',
        trackId,
        currentTime: audio.currentTime,
        duration: audio.duration,
      });
    };
    const onEnded = () => {
      if (!this.tracks.has(trackId)) return;
      if (isSafariPatchInProgress(audio)) return;
      this.handleTrackEnded(trackId);
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);

    const cleanupListeners = () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };

    const entry: TrackEntry = { id: trackId, audio, type, chatId, cleanupListeners, refCount: 1 };

    this.tracks.set(trackId, entry);
    this.addToQueue(trackId, type);
  }

  unregisterTrack(trackId: TrackId): void {
    const entry = this.tracks.get(trackId);
    if (!entry) return;

    entry.refCount--;
    if (entry.refCount > 0) return; // Other consumers still using this track

    const wasCurrentAndPlaying = this.currentTrackId === trackId && !entry.audio.paused;

    if (!entry.audio.paused) {
      entry.audio.pause();
    }

    entry.cleanupListeners();

    this.tracks.delete(trackId);
    this.trackListeners.delete(trackId);
    // Keep trackId in queue — virtualized list may unmount components,
    // but we still need queue navigation for auto-advance

    if (this.currentTrackId === trackId) {
      this.currentTrackId = undefined;
      // The DOM `pause` event fires async, AFTER cleanupListeners() above removed its forwarder —
      // so without an explicit emit, global subscribers (header bar, panels) stay stuck "playing".
      if (wasCurrentAndPlaying) {
        this.emit({ type: 'stop' });
      }
    }

    // Release the element's network/decoder resources — detached elements with a src keep
    // buffered data alive until GC decides otherwise.
    entry.audio.removeAttribute('src');
    entry.audio.load();
  }

  // ─── Playback ──────────────────────────────────────────────────────────────

  play(trackId: TrackId, src: string): void {
    const entry = this.tracks.get(trackId);
    if (!entry) return;

    // Pause old track atomically
    if (this.currentTrackId && this.currentTrackId !== trackId) {
      const old = this.tracks.get(this.currentTrackId);
      if (old) {
        if (isSafariPatchInProgress(old.audio)) {
          old.audio.dataset.preventPlayAfterPatch = 'true';
        }
        old.audio.pause();
        old.audio.currentTime = 0;
      }
      this.emit({ type: 'trackChange', prevTrackId: this.currentTrackId, nextTrackId: trackId });
    }

    this.currentTrackId = trackId;
    entry.src = src;

    const { audio } = entry;
    // Assign when the element has no src OR the src changed (e.g. a refreshed stream URL, or a
    // restored track whose persisted URL was stale) — the old `!audio.src` guard could never
    // replace a dead URL, leaving the track unplayable.
    if (audio.src !== src) {
      audio.src = src;
      audio.preload = 'auto';

      if (src.includes('/progressive/') && IS_SAFARI) {
        delete audio.dataset.preventPlayAfterPatch;
        patchSafariProgressiveAudio(audio);
      }
    }

    // Reset to start if track ended (currentTime at duration)
    if (audio.ended) {
      audio.currentTime = 0;
    }

    const playPromise = audio.play();
    if (playPromise) {
      playPromise.catch((err: unknown) => {
        if (err instanceof Error && err.name === 'NotAllowedError') {
          // Blocked by the autoplay policy (play() ran outside a user gesture) — retry on the next
          // interaction instead of failing silently, so audio actually starts.
          this.retryPlayOnGesture(trackId);
          return;
        }
        this.emit({ type: 'error', trackId, error: err });
      });
    }
  }

  pause(trackId: TrackId): void {
    if (this.currentTrackId !== trackId) return;
    const entry = this.tracks.get(trackId);
    entry?.audio.pause();
  }

  pauseCurrent(): void {
    if (!this.currentTrackId) return;
    this.pause(this.currentTrackId);
  }

  /** True (once) if `trackId` was restored from a paused session. The autoplay effect calls this to
   *  skip its restore autoplay, without blocking a genuine user-initiated play(). */
  consumeAutoplaySuppression(trackId: TrackId): boolean {
    if (trackId !== this.restoredPausedTrackId) return false;
    this.restoredPausedTrackId = undefined;
    return true;
  }

  /** Play a short silent clip on the first user gesture. Our play() runs from a React effect (after
   *  an await/render), i.e. outside the click's transient activation, so on a low-media-engagement
   *  origin (localhost/dev) the browser's autoplay policy blocks it and audio never starts. Priming
   *  a real play() inside a genuine gesture marks the element pool as user-activated. */
  unlockAutoplay(): void {
    if (this.isAutoplayUnlocked) return;
    this.isAutoplayUnlocked = true;
    const silent = new Audio(
      'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=',
    );
    silent.play().then(() => {
      silent.pause();
      silent.src = '';
    }).catch(() => {
      // Still blocked — let a later gesture try again.
      this.isAutoplayUnlocked = false;
    });
  }

  private pendingGestureRetry: (() => void) | undefined;

  /** Retry the current track once on the next user gesture. Called when play() was blocked by the
   *  autoplay policy: the retry runs synchronously inside the gesture, so it's allowed. Only one
   *  retry is armed at a time — a newer blocked track replaces the previous listeners. */
  private retryPlayOnGesture(trackId: TrackId): void {
    if (typeof window === 'undefined') return;
    if (this.pendingGestureRetry) this.pendingGestureRetry(); // Detach previous listeners

    const detach = () => {
      window.removeEventListener('pointerdown', retry, true);
      window.removeEventListener('keydown', retry, true);
      window.removeEventListener('touchend', retry, true);
      this.pendingGestureRetry = undefined;
    };
    const retry = () => {
      detach();
      if (this.currentTrackId !== trackId) return;
      const entry = this.tracks.get(trackId);
      if (entry && entry.audio.paused) entry.audio.play().catch(() => {});
    };
    this.pendingGestureRetry = detach;
    window.addEventListener('pointerdown', retry, true);
    window.addEventListener('keydown', retry, true);
    window.addEventListener('touchend', retry, true);
  }

  stop(trackId: TrackId): void {
    if (this.currentTrackId !== trackId) return;
    const entry = this.tracks.get(trackId);
    if (!entry) return;

    entry.audio.pause();
    entry.audio.currentTime = 0;
    this.emit({ type: 'stop' });
  }

  seekTo(trackId: TrackId, time: number): void {
    if (this.currentTrackId !== trackId) return;
    const entry = this.tracks.get(trackId);
    if (!entry) return;

    // Clamp into [0, duration] so scrubbing past the end can't fire a spurious `ended`/auto-advance.
    const { duration } = entry.audio;
    const time_ = Number.isFinite(duration) && duration > 0
      ? Math.max(0, Math.min(time, duration))
      : Math.max(0, time);
    if (entry.audio.fastSeek) {
      entry.audio.fastSeek(time_);
    } else {
      entry.audio.currentTime = time_;
    }
  }

  setVolume(trackId: TrackId, volume: number): void {
    if (this.currentTrackId !== trackId) return;
    const entry = this.tracks.get(trackId);
    if (!entry) return;
    entry.audio.volume = volume;
    entry.audio.muted = false;
  }

  setPlaybackRate(trackId: TrackId, rate: number): void {
    if (this.currentTrackId !== trackId) return;
    const entry = this.tracks.get(trackId);
    if (entry) {
      entry.audio.playbackRate = rate;
    }
  }

  setMuted(trackId: TrackId, muted: boolean): void {
    const entry = this.tracks.get(trackId);
    if (entry) {
      entry.audio.muted = muted;
    }
  }

  toggleMuted(trackId: TrackId): void {
    const entry = this.tracks.get(trackId);
    if (entry) {
      entry.audio.muted = !entry.audio.muted;
    }
  }

  // ─── Queue Management ─────────────────────────────────────────────────────

  addToQueue(trackId: TrackId, type: TrackEntry['type']): void {
    const queue = this.getQueueForType(type);
    if (!queue) return;
    this.insertSorted(queue, trackId);

    // Bound the queue: scrolling through thousands of voice/audio messages must not grow it
    // forever. Evict from the far end relative to the current track so navigation nearby survives.
    if (queue.length > MAX_QUEUE_SIZE) {
      const currentIndex = this.currentTrackId ? queue.indexOf(this.currentTrackId) : -1;
      if (currentIndex >= 0 && currentIndex < queue.length / 2) {
        queue.pop();
      } else {
        queue.shift();
      }
    }
  }

  /** Binary insertion keeping the queue sorted — replaces the old includes() + full sort()
   *  (O(n²·log n) cumulative while scrolling a long chat). */
  private insertSorted(queue: TrackId[], trackId: TrackId): void {
    let lo = 0;
    let hi = queue.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const cmp = trackIdComparator(queue[mid], trackId);
      if (cmp === 0) return; // Already present
      if (cmp < 0) lo = mid + 1;
      else hi = mid;
    }
    if (queue[lo] === trackId) return;
    queue.splice(lo, 0, trackId);
  }

  removeFromQueue(trackId: TrackId): void {
    this.musicQueue = this.musicQueue.filter((id) => id !== trackId);
    this.voiceQueue = this.voiceQueue.filter((id) => id !== trackId);
  }

  getQueue(type: TrackEntry['type']): TrackId[] {
    if (type === 'audio') return [...this.musicQueue];
    if (type === 'voice') return [...this.voiceQueue];
    return [];
  }

  findNext(
    trackId: TrackId,
    origin: AudioOrigin,
    reverse = false,
  ): TrackId | undefined {
    const entry = this.tracks.get(trackId);
    if (!entry) return undefined;

    const queue = this.getQueueForType(entry.type);
    if (!queue || queue.length === 0) return undefined;

    // Shuffle: pick random track (not current)
    if (this.shuffle && !reverse) {
      const others = queue.filter((id) => id !== trackId);
      if (others.length === 0) return this.repeatMode === 'all' ? trackId : undefined;
      return others[Math.floor(Math.random() * others.length)];
    }

    let pool: TrackId[];
    if (origin === AudioOrigin.Search) {
      pool = queue;
    } else {
      const chatId = entry.chatId;
      pool = queue.filter((id) => id.startsWith(`msg${chatId}`));
    }

    const index = pool.indexOf(trackId);
    if (index < 0) return undefined;

    let direction = (origin === AudioOrigin.Inline) ? -1 : 1;
    if (reverse) direction *= -1;
    const nextIndex = index + direction;

    // Wrap around for repeat-all
    if (this.repeatMode === 'all' && (nextIndex < 0 || nextIndex >= pool.length)) {
      return pool[nextIndex < 0 ? pool.length - 1 : 0];
    }

    return pool[nextIndex];
  }

  /**
   * Returns the next trackId to play, or undefined if none.
   * Caller must handle the case where the track isn't registered
   * (e.g. dispatch openAudioPlayer to load it).
   */
  playNext(trackId: TrackId, origin: AudioOrigin, reverse = false): TrackId | undefined {
    const nextId = this.findNext(trackId, origin, reverse);
    if (!nextId) return undefined;

    const nextEntry = this.tracks.get(nextId);
    if (!nextEntry) {
      // Track in queue but not mounted (virtualized list) — caller handles
      return nextId;
    }

    const nextSrc = nextEntry.src || nextEntry.audio.src;
    if (nextSrc) {
      this.play(nextId, nextSrc);
    }
    return nextId;
  }

  playPrev(trackId: TrackId, origin: AudioOrigin): void {
    this.playNext(trackId, origin, true);
  }

  /**
   * Prune queue to only keep tracks matching the given chatIds.
   * Call this explicitly when changing context (e.g. closing search).
   */
  pruneQueue(type: TrackEntry['type'], allowedChatIds: string[]): void {
    const filter = (id: TrackId) => {
      const chatId = parseTrackChatId(id);
      return allowedChatIds.includes(chatId);
    };

    if (type === 'audio') {
      this.musicQueue = this.musicQueue.filter(filter);
    } else if (type === 'voice') {
      this.voiceQueue = this.voiceQueue.filter(filter);
    }
  }

  private handleTrackEnded(trackId: TrackId): void {
    if (this.repeatMode === 'one') {
      // Replay same track
      const entry = this.tracks.get(trackId);
      if (entry) {
        entry.audio.currentTime = 0;
        entry.audio.play().catch((err: unknown) => {
          // A failed repeat must not die silently — surface it so the UI can reflect the stop.
          this.emit({ type: 'error', trackId, error: err });
        });
        return; // Don't emit ended — track restarts
      }
    }
    this.emit({ type: 'ended', trackId });
    // For 'all' and 'none' — caller (useAudioPlayer ended handler) handles advance
  }

  // ─── Getters ───────────────────────────────────────────────────────────────

  get currentTrack(): TrackId | undefined {
    return this.currentTrackId;
  }

  get isPlaying(): boolean {
    if (!this.currentTrackId) return false;
    const entry = this.tracks.get(this.currentTrackId);
    return entry !== undefined && !entry.audio.paused;
  }

  get progress(): number {
    if (!this.currentTrackId) return 0;
    const entry = this.tracks.get(this.currentTrackId);
    if (!entry || !entry.audio.duration) return 0;
    return entry.audio.currentTime / entry.audio.duration;
  }

  get duration(): number {
    if (!this.currentTrackId) return 0;
    return this.tracks.get(this.currentTrackId)?.audio.duration ?? 0;
  }

  get currentAudio(): HTMLAudioElement | undefined {
    if (!this.currentTrackId) return undefined;
    return this.tracks.get(this.currentTrackId)?.audio;
  }

  currentTime(trackId: TrackId): number {
    return this.tracks.get(trackId)?.audio.currentTime ?? 0;
  }

  hasTrack(trackId: TrackId): boolean {
    return this.tracks.has(trackId);
  }

  /** Replace music queue with exact set of trackIds (for playlist from filtered list) */
  setMusicPlaylist(trackIds: TrackId[]): void {
    this.musicQueue = [...trackIds];
    this.musicQueue.sort(trackIdComparator);
    this.saveState();
  }

  /** Pre-set src on a registered track without playing it */
  setTrackSrc(trackId: TrackId, src: string): void {
    const entry = this.tracks.get(trackId);
    if (entry && entry.src !== src) {
      entry.src = src;
      entry.audio.src = src;
      entry.audio.preload = 'auto';
    }
  }

  getTrackEntry(trackId: TrackId): TrackEntry | undefined {
    return this.tracks.get(trackId);
  }

  // ─── Persistence ──────────────────────────────────────────────────────────

  private static STORAGE_KEY = 'audioPlayer_state';

  saveState(): void {
    try {
      const currentEntry = this.currentTrackId ? this.tracks.get(this.currentTrackId) : undefined;
      const state = {
        currentTrackId: this.currentTrackId,
        currentTime: currentEntry?.audio.currentTime || 0,
        wasPlaying: currentEntry ? !currentEntry.audio.paused : false,
        repeatMode: this.repeatMode,
        shuffle: this.shuffle,
        // Save playlist with src URLs for instant restore
        // Persist queue IDS for navigation; srcs only when stable http(s) (blob:/object URLs are
        // dead after a reload). Bounded — this runs on every play/pause, and an unbounded playlist
        // would grow localStorage and slow down each write.
        playlist: this.musicQueue.slice(0, MAX_PERSISTED_PLAYLIST).map((id) => {
          const entry = this.tracks.get(id);
          const src = entry?.src || entry?.audio.src || '';
          return {
            id,
            type: entry?.type || 'audio',
            src: src.startsWith('blob:') ? '' : src,
          };
        }),
      };
      localStorage.setItem(AudioController.STORAGE_KEY, JSON.stringify(state));
    } catch { /* ignore quota errors */ }
  }

  restoreState(): void {
    try {
      const raw = localStorage.getItem(AudioController.STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw);

      this.repeatMode = state.repeatMode || 'none';
      this.shuffle = state.shuffle || false;

      // Restore the queue as BARE IDS only — no registerTrack/Audio-element creation. Registering
      // every playlist entry here leaked one detached HTMLAudioElement (+4 listeners) per track for
      // the whole session; navigation only needs the ids (playNext/openAudioPlayer re-register
      // tracks on demand when their component mounts).
      if (state.playlist?.length) {
        this.musicQueue = state.playlist
          .map((t: { id: string }) => t.id as TrackId)
          .filter(Boolean)
          .slice(0, MAX_PERSISTED_PLAYLIST);
        this.musicQueue.sort(trackIdComparator);
      }

      // Restore ONLY the current track as a live entry (it's the one the player re-opens).
      if (state.currentTrackId) {
        const saved = (state.playlist || []).find((t: { id: string }) => t.id === state.currentTrackId);
        this.currentTrackId = state.currentTrackId as TrackId;
        if (saved?.src) {
          this.registerTrack(this.currentTrackId, saved.type || 'audio');
          this.setTrackSrc(this.currentTrackId, saved.src);
          if (state.currentTime) {
            this.tracks.get(this.currentTrackId)!.audio.currentTime = state.currentTime;
          }
        }

        // If it was paused when saved, suppress the one restore autoplay so it stays paused.
        if (state.wasPlaying === false) {
          this.restoredPausedTrackId = this.currentTrackId;
        }
      }
    } catch { /* ignore parse errors */ }
  }

  clearSavedState(): void {
    localStorage.removeItem(AudioController.STORAGE_KEY);
  }

  /** Returns saved track info for UI restoration (dispatch openAudioPlayer) */
  getSavedTrackInfo(): { chatId: string; messageId: number } | undefined {
    if (!this.currentTrackId) return undefined;
    try {
      const { messageKey } = splitTrackId(this.currentTrackId);
      const parsed = parseMessageKey(messageKey);
      return { chatId: parsed.chatId, messageId: parsed.messageId };
    } catch {
      return undefined;
    }
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  private getQueueForType(type: TrackEntry['type']): TrackId[] | undefined {
    if (type === 'audio') return this.musicQueue;
    if (type === 'voice') return this.voiceQueue;
    return undefined;
  }
}

// ─── Singleton ────────────────────────────────────────────────────────────────

export const audioController = new AudioController();

// Restore saved playback state on load
audioController.restoreState();

if (typeof window !== 'undefined') {
  // Unlock autoplay on the first user gesture (see unlockAutoplay) so effect-driven playback works
  // on low-engagement origins like localhost/dev.
  const unlock = () => audioController.unlockAutoplay();
  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock);
  window.addEventListener('touchend', unlock, { passive: true });

  // Save state before page unload (captures final currentTime)
  window.addEventListener('beforeunload', () => {
    audioController.saveState();
  });
  (window as any).__audioController = audioController;
}
